import appSettings from 'wanderu-config';
import { i18nMoment } from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import { PricingService } from 'wanderu-service-pricing';

import {
    setAllCitiesInactive,
    fetchPopularCityInfo,
    setCityActive,
    validCitiesSelector,
} from './PopularCities';
import {
    setSearching,
    setNoResults,
    setHasResults,
} from './Status';

const logger = Logger.getLogger('Pricings');

const NAMESPACE = 'explorer/pricings';

const SET_ACTIVE_WCITYID = `${NAMESPACE}/SET_ACTIVE_WCITYID`;
const SET_CITY_PRICING = `${NAMESPACE}/SET_CITY_PRICING`;
const REMOVE_PRICINGS = `${NAMESPACE}/REMOVE_PRICINGS`;
const SET_PRICINGS_FETCHING = `${NAMESPACE}/SET_PRICINGS_FETCHING`;
const REMOVE_PRICINGS_FETCHING = `${NAMESPACE}/REMOVE_PRICINGS_FETCHING`;

const INITIAL_STATE = {
    fetching: false,
    cities: {},
    activeWcityid: undefined,
};

export function fetchLowestPrices(activeWcityid, cityToCityIds, date) {
    return async (dispatch, getState) => {
        dispatch(setSearching());
        dispatch(setPricingsFetching());

        dispatch(setAllCitiesInactive());
        dispatch(setActiveWcityid(activeWcityid));

        if (!cityToCityIds) {
            return dispatch(setNoResults());
        }

        const aggregatePriceIterator = _makeAggregatePricesIterator(cityToCityIds, date);
        const numToFetch = Math.min(NUM_CITY_PRICING_TO_FETCH, cityToCityIds.length);
        const cityToCityIdsToFetch = cityToCityIds.slice(0, numToFetch);

        let state = getState();

        await Promise.all(cityToCityIdsToFetch.map(async (cityToCityId) => {
            try {
                const results = await _recursiveAggregatePriceFetch(aggregatePriceIterator);
                const wcityid = cityToCityId.split('_')[1];

                results.cheapestMode = _getCheapestMode(results);
                dispatch(setCityPricing(wcityid, results));

                // TODO: Selector?
                if (state.explorer.popularCities[wcityid]) {
                    return dispatch(setCityActive(wcityid));
                }

                return dispatch(fetchPopularCityInfo(wcityid));
            } catch (ex) {
                const domainUserInfo = appSettings.env.isClient
                    ? SnowplowService.getDomainUserInfo()
                    : {};

                logger.error('fetchLowestPrices', ex, {
                    snowplowIds: {
                        networkUserId: SnowplowService.getNetworkUserId(),
                        ...domainUserInfo,
                    },
                });
            }
        }));

        state = getState();
        const validCities = validCitiesSelector(state);
        dispatch(_setResultStatus(validCities));

        return dispatch(removePricingsFetching());
    };
}

export function removePricings() {
    return { type: REMOVE_PRICINGS };
}

export function removePricingsFetching() {
    return { type: REMOVE_PRICINGS_FETCHING };
}

export function setActiveWcityid(activeWcityid) {
    return { type: SET_ACTIVE_WCITYID, activeWcityid };
}

export function setCityPricing(wcityid, result) {
    return { type: SET_CITY_PRICING, wcityid, result };
}

function _setActiveWcityid(state, activeWcityid) {
    if (activeWcityid) {
        return { ...state, activeWcityid };
    }

    return state;
}

function _setCityPricing(state, wcityid, results) {
    if (wcityid && results) {
        return {
            ...state,
            cities: {
                ...state.cities,
                [wcityid]: results,
            },
        };
    }
    return state;
}

function _removePricings(state) {
    return INITIAL_STATE;
}

function _setPricingsFetching(state) {
    return { ...state, fetching: true };
}

function _removePricingsFetching(state) {
    return { ...state, fetching: false };
}

export default function reducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case SET_ACTIVE_WCITYID:
            return _setActiveWcityid(state, action.activeWcityid);
        case SET_CITY_PRICING:
            return _setCityPricing(state, action.wcityid, action.result);
        case REMOVE_PRICINGS:
            return _removePricings(state);
        case SET_PRICINGS_FETCHING:
            return _setPricingsFetching(state);
        case REMOVE_PRICINGS_FETCHING:
            return _removePricingsFetching(state);
        default:
            return state;
    }
}

const NUM_CITY_PRICING_TO_FETCH = 12;
export function setPricingsFetching() {
    return { type: SET_PRICINGS_FETCHING };
}

function _setResultStatus(validCities) {
    return (dispatch) => {
        if (validCities.length === 0) {
            return dispatch(setNoResults());
        }
        dispatch(setHasResults(validCities.length));
    };
}

function _makeAggregatePricesIterator(cityToCityIds, date) {
    let nextIndex = 0;

    return {
        next() {
            const fromDate = date
                ? i18nMoment(date)
                : i18nMoment();
            const daysToAdd = date
                ? 1
                : 31;
            const toDate = i18nMoment(fromDate).add(daysToAdd, 'days');

            const cityToCityId = cityToCityIds[nextIndex];
            nextIndex += 1;

            if (nextIndex === cityToCityIds.length) {
                return { done: true };
            }

            return {
                value: PricingService.getAggregatePricesForDateRange(
                    cityToCityId,
                    fromDate,
                    toDate,
                ),
                done: false,
            };
        },
    };
}

async function _recursiveAggregatePriceFetch(aggregatePriceIterator) {
    try {
        const {
            value,
            done,
        } = aggregatePriceIterator.next();

        if (done) {
            throw new Error('All cityToCity aggregate prices have been fetched');
        }

        return await value;
    } catch (ex) {
        const domainUserInfo = appSettings.env.isClient
            ? SnowplowService.getDomainUserInfo()
            : {};

        logger.error('_recursiveAggregatePriceFetch', ex, {
            snowplowIds: {
                networkUserId: SnowplowService.getNetworkUserId(),
                ...domainUserInfo,
            },
        });
        throw ex;
    }
}

function _getCheapestMode(pricing) {
    const modes = Object.keys(pricing);

    return modes.reduce((leastMode, currMode) => {
        const leastPrice = pricing[leastMode].minPrice;
        const currPrice = pricing[currMode].minPrice;

        if (currPrice < leastPrice) {
            return currMode;
        }

        return leastMode;
    }, modes[0]);
}
